import { useState, useEffect, useCallback, useMemo } from 'react';
import { AppNordModal } from '@/components/ui/AppNordModal';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Appointment } from '@/types/appointment-types';
import TimeSlotPicker from './TimeSlotPicker';
import { useQueryClient } from '@tanstack/react-query';
import { useAvailableSlots } from '@/hooks/use-schedules'; // Corrected hook import
// import { fetchProviderById } from '@/lib/supabase-patient-api'; // Function does not exist
import Loading from '@/components/ui/Loading';
import { useToast } from '@/components/ui/use-toast';
import { useAppointments } from '@/hooks/use-appointments';
import { useScheduleAppointment } from '@/hooks/use-schedule-appointment';
import { format, parseISO } from 'date-fns'; // Restored date-fns import
import { DateRange } from 'react-day-picker'; // Restored DateRange import

// Define TimeSlot interface locally since it's not exported from appointment-types
interface TimeSlot {
  id: string;
  providerId: string;
  locationId: string;
  startTime: string;
  endTime: string;
  available: boolean;
}

export interface RescheduleAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: Appointment;
}

export const RescheduleAppointmentModal: React.FC<RescheduleAppointmentModalProps> = ({ appointment, isOpen, onClose }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { updateAppointment, isLoading: _isUpdating } = useAppointments();
  const [selectedDate, _setSelectedDate] = useState<Date>(appointment.startTime ? parseISO(appointment.startTime) : new Date());
  const [selectedLocationId, _setSelectedLocationId] = useState<string | null>(appointment.facilityId || null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [_error, _setError] = useState<string | null>(null);
  // Removed duplicate/incorrect state declarations

  const {
    timeSlots: _timeSlots,
    selectedDate: scheduleSelectedDate,
    selectedTimeSlot: scheduleSelectedTimeSlot,
    selectDate,
    selectTimeSlot: _selectTimeSlot,
    isLoadingTimeSlots,
    timeSlotsError,
  } = useScheduleAppointment();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [_availableSlotsLoading, _setAvailableSlotsLoading] = useState(false); // Added loading state

  // Format date for API calls/query keys
  const formattedDate = useMemo(() => format(selectedDate, 'yyyy-MM-dd'), [selectedDate]);

  // Fetch available slots when date or location changes
  const { data: availableSlots = [], isLoading: _slotsLoadingFromHook } = useAvailableSlots(
    appointment.providerId || undefined,
    formattedDate,
    {
      locationId: selectedLocationId || undefined,
      enabled: !!appointment.providerId && !!selectedLocationId
    }
  );

  useEffect(() => {
    if (isOpen && appointment) {
      const apptDate = parseISO(appointment.startTime);
      selectDate(apptDate);
    }
  }, [isOpen, appointment.startTime, selectDate]);

  const displaySelectedDate = scheduleSelectedDate || selectedDate;

  // Convert available slots (AppointmentSlot[]) to time strings for the picker
  const availableTimeSlotStrings = useMemo(() => {
      if (!availableSlots) return [];
      // Filter out slots without a valid startTime before mapping
      return availableSlots
          .filter(slot => slot && slot.startTime)
          .map(slot => format(parseISO(slot.startTime), 'h:mm a'));
  }, [availableSlots]);

  // Get the time string for the selected slot
  const selectedTimeSlotString = scheduleSelectedTimeSlot
    ? format(parseISO(scheduleSelectedTimeSlot.startTime), 'h:mm a')
    : '';

  // Handle selecting a time slot (expects string from picker)
  const handleTimeSlotSelect = useCallback((timeString: string) => {
      // Find the original AppointmentSlot object based on the formatted time string
      const selected = availableSlots.find(slot => slot && slot.startTime && format(parseISO(slot.startTime), 'h:mm a') === timeString);

      if (selected) {
          // Construct the local TimeSlot object for state
          // Assuming AppointmentSlot has slotId, doctorId, facilityId
          setSelectedTimeSlot({
              id: selected.slotId || `temp-${Date.now()}`,
              startTime: selected.startTime,
              endTime: selected.endTime,
              available: selected.isAvailable,
              providerId: selected.doctorId,
              locationId: selected.facilityId,
          });
      } else {
          setSelectedTimeSlot(null);
      }
  }, [availableSlots]);

  // Calendar onSelect handler with proper type handling
  const handleCalendarSelect = (value: Date | Date[] | DateRange | undefined) => {
    // For single date selection (most common case)
    if (value instanceof Date) {
      selectDate(value);
    }
    // For array of dates, use first date if available
    else if (Array.isArray(value) && value.length > 0) {
      selectDate(value[0]);
    }
    // For date range, use 'from' date if available
    else if (value && typeof value === 'object' && 'from' in value && value.from) {
      selectDate(value.from);
    }
    // For undefined or other cases, pass undefined
    else {
      selectDate(undefined);
    }
  };

  // Handle form submission
  const handleReschedule = async () => {
    if (!selectedDate || !selectedTimeSlot) {
      toast({
        title: "Error",
        description: "Please select both a date and time slot",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Extract date from selected date and time from selected timeslot
      const appointmentDate = format(selectedDate, 'yyyy-MM-dd');
      const appointmentTime = format(parseISO(selectedTimeSlot.startTime), 'HH:mm:ss');

      // Use snake_case keys matching the database schema
      await updateAppointment({
        id: appointment.id,
        preferred_date: appointmentDate,
        preferred_time: appointmentTime,
        status: 'rescheduled',
        provider_id: selectedTimeSlot.providerId,
        clinic_location: selectedTimeSlot.locationId,
        slot_id: selectedTimeSlot.id
      });
      
      toast({
        title: "Success",
        description: "Appointment successfully rescheduled",
      });
      
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      onClose();
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      toast({
        title: "Error",
        description: (error as Error).message || "Failed to reschedule appointment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppNordModal
      isOpen={isOpen}
      onClose={onClose}
      heading="Reschedule Appointment"
      size="l"
    >
      <div className="space-y-4">
        <div className="text-sm text-muted-foreground mb-4">
          Rescheduling appointment for: {appointment.appointmentType} on {format(parseISO(appointment.startTime), 'PPP p')}
        </div>

        <div className="mb-4">
          <h3 className="text-sm font-medium mb-2">Select a new date</h3>
          <Calendar
            mode="single"
            selected={displaySelectedDate}
            onSelect={handleCalendarSelect}
            className="rounded-md border"
            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
          />
        </div>

        {selectedDate && (
          <div className="mb-4">
            <h3 className="text-sm font-medium mb-2">
              Select a new time slot for {format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </h3>
            {isLoadingTimeSlots ? (
              <Loading text="Loading available slots..." />
            ) : timeSlotsError ? (
              <p className="text-sm text-destructive">Error loading time slots.</p>
            ) : availableTimeSlotStrings.length === 0 ? (
              <p className="text-sm text-muted-foreground">No available time slots for this date. Please select another date.</p>
            ) : (
              <TimeSlotPicker
                availableTimeSlots={availableTimeSlotStrings}
                selectedTimeSlot={selectedTimeSlotString}
                onSelectTimeSlot={handleTimeSlotSelect}
              />
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleReschedule}
            disabled={!selectedDate || !selectedTimeSlot || isSubmitting}
          >
            {isSubmitting ? 'Rescheduling...' : 'Confirm Reschedule'}
          </Button>
        </div>
      </div>
    </AppNordModal>
  );
}

export default RescheduleAppointmentModal; 